import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import StationBasicDetails from '../components/EachStationDetail/StationBasicDetails';
import Graphs from '../components/Graphs';
import useDaywiseTotalRevenueByStation from '../hooks/useDaywiseTotalRevenueByStation';
import useDaywiseTotalEnergyByStation from '../hooks/useDaywiseTotalEnergyByStation';
import useDaywiseTotalDurationByStation from '../hooks/useDaywiseTotalDurationByStation';
import useDaywiseSessionsByStation from '../hooks/useDaywiseSessionsByStation';
import ChargingSessionsDesktop from '../components/ChargingSessionsDesktop';
import ChargingSessionsMobile from '../components/ChargingSessionsMobile';
import PaymentTransactionsDesktop from '../components/EachStationDetail/PaymentTransactionsDesktop';
import PaymentTransactionsMobile from '../components/EachStationDetail/PaymentTransactionsMobile';
import StationTiles from './StationTiles';
import DatePicker from '../components/DatePicker';
import DateWiseStationData from '../components/EachStationDetail/DateWiseStationData';

// helper to generate ranges
const generateRange = (daysBack) => {
  const today = new Date();
  const from = new Date();
  from.setDate(today.getDate() - (daysBack - 1));
  return {
    fromDate: from.toISOString().split('T')[0],
    toDate: today.toISOString().split('T')[0],
  };
};

const StationDetails = () => {
  const { stationId } = useParams();
  const [selectedMetric, setSelectedMetric] = useState('Total revenue');
  // const [range, setRange] = useState(null);
  const [range, setRange] = useState(generateRange(30));

  const [selectedView, setSelectedView] = useState('sessions');

  // Hooks now use range instead of daysCount
  const { revenueData, isLoading: revenueLoading, error: revenueError } =
    useDaywiseTotalRevenueByStation(stationId, range);
  const { energyData, isLoading: energyLoading } =
    useDaywiseTotalEnergyByStation(stationId, range);
  const { durationData, isLoading: durationLoading } =
    useDaywiseTotalDurationByStation(stationId, range);
  const { sessionData: sessions, isLoading: sessionsLoading } =
    useDaywiseSessionsByStation(stationId, range);

    const transformApiData = (apiData, metricType) => {
      if (!apiData || !Array.isArray(apiData)) return [];
  
      const today = new Date();
      today.setHours(0, 0, 0, 0);
  
      return apiData
        .map((item) => {
          const dateString = item.session_date || item.SessionDate;
          if (!dateString) return null;
  
          let date;
          try {
            date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
          } catch {
            return null;
          }
  
          const daysAgo = Math.floor((today - date) / (1000 * 60 * 60 * 24));
  
          let value;
          let unit;
  
          switch (metricType) {
            case 'Total revenue':
              value = parseFloat((item.total_revenue || 0).toFixed(1));
              unit = '₹';
              break;
            case 'total energy':
            case 'Total energy (kW)':
              value = parseFloat((item.total_energy_kwh || item.TotalEnergy || 0).toFixed(1));
              unit = 'kWh';
              break;
            case 'Total sessions':
              value = parseFloat((item.session_count || item.SessionCount || 0).toFixed(1));
              unit = 'sessions';
              break;
            case 'Total hours':
              const seconds = item.total_duration_seconds || item.TotalDuration || 0;
              const minutes = seconds / 60;
              const hours = minutes / 60;
              value = parseFloat(hours.toFixed(1));
              unit = 'hrs';
              break;
            default:
              value = 0;
              unit = '';
          }
  
          return {
            day: Math.max(1, daysAgo + 1),
            value: value,
            label: `${value} ${unit}`,
          };
        })
        .filter((item) => item !== null);
    };

    const getApiDataForMetric = () => {
      const metric = (selectedMetric || '').toLowerCase();
      if (metric.includes('revenue')) return transformApiData(revenueData, 'Total revenue');
      if (metric.includes('energy')) return transformApiData(energyData, 'Total energy (kW)');
      if (metric.includes('session')) return transformApiData(sessions, 'Total sessions');
      if (metric.includes('hour')) return transformApiData(durationData, 'Total hours');
      return transformApiData(revenueData, 'Total revenue');
    };
    

  const apiData = getApiDataForMetric();

  if (revenueError) {
    console.error('Station revenue data error:', revenueError);
  }

  const calcDaysCount = (fromDate, toDate) => {
    const start = new Date(fromDate);
    const end = new Date(toDate);
    return Math.max(1, Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1);
  };
  
  const daysCount = range?.fromDate && range?.toDate
    ? calcDaysCount(range.fromDate, range.toDate)
    : 30; // default


  return (
    <div className="w-full min-w-0">
          <div className="flex flex-col gap-4">
            {/* Station Basic Info */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl p-6">
              <StationBasicDetails stationId={stationId} />
            </div>

            {/* Main Analytics Section */}
            <div className="grid grid-cols-1 gap-4">
              <div className="lg:col-span-3">
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl p-6 w-full h-full">
                  {/* Range Filter */}
                  <div className="hidden lg:flex items-center gap-3 mb-6 relative">
                    {[
                      { label: 'Today', range: generateRange(1) },
                      { 
                        label: 'Yesterday', 
                        range: (() => {
                          const today = new Date();
                          const yesterday = new Date();
                          yesterday.setDate(today.getDate() - 1);
                          const y = yesterday.toISOString().split('T')[0];
                          return { fromDate: y, toDate: y };
                        })()
                      },
                      { label: 'Last 30 Days', range: generateRange(30) },
                      { label: 'Last 90 Days', range: generateRange(90) },
                      { label: 'Last 1 Year', range: generateRange(365) },
                    ].map((r) => (
                      <button
                        key={r.label}
                        onClick={() => setRange(r.range)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                          range?.fromDate === r.range.fromDate && range?.toDate === r.range.toDate
                            ? 'bg-green-600 text-white border-green-600 shadow-sm'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}

                    {/* Date Picker */}
                    <DatePicker
                      onApply={(range) => setRange(range)}
                    />
                    {range && (
                      <span className="ml-4 text-sm text-gray-700">
                        From: <strong>{range.fromDate}</strong> To: <strong>{range.toDate}</strong>
                      </span>
                    )}
                  </div>

                  {/* Mobile dropdown */}
                  <div className="lg:hidden mb-4">
                    {(() => {
                      const options = [
                        { label: 'Today', range: generateRange(1) },
                        { label: 'Last 30 Days', range: generateRange(30) },
                        { label: 'Last 60 Days', range: generateRange(60) },
                        { label: 'Last 90 Days', range: generateRange(90) },
                        { label: 'Last 1 Year', range: generateRange(365) },
                      ];
                      const current = options.find(
                        (r) => r.range.fromDate === range?.fromDate && r.range.toDate === range?.toDate
                      );
                      return (
                        <select
                          value={current ? current.label : ''}
                          onChange={(e) => {
                            const picked = options.find((r) => r.label === e.target.value);
                            if (picked) setRange(picked.range);
                          }}
                          className="w-full p-2 rounded-xl border border-gray-300 text-sm bg-white"
                        >
                          {!current && <option value="" disabled>Custom range</option>}
                          {options.map((r) => (
                            <option key={r.label} value={r.label}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                  </div>

                  {/* Metrics */}
                  <StationTiles
                    tiles={[]}
                    onTileClick={setSelectedMetric}
                    range={range}
                    stationId={stationId}
                  />

                  {/* Graph */}
                  <div className="mt-6 w-full">
                    <Graphs
                      selected={selectedMetric}
                      apiData={apiData}
                      customRange={range}
                      stationId={stationId}
                      daysCount = {daysCount}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Sessions or Transactions List */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
              <h2 className="text-xl font-medium">
                  {selectedView === 'sessions'
                    ? 'Charging Sessions'
                    : selectedView === 'transactions'
                    ? 'Payment Transactions'
                    : 'Daywise Charging Summary'}
                </h2>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedView('sessions')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      selectedView === 'sessions'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Sessions
                  </button>
                  <button
                    onClick={() => setSelectedView('transactions')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      selectedView === 'transactions'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Transactions
                  </button>
                  <button
                    onClick={() => setSelectedView('Daywise Report')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      selectedView === 'Daywise Report'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >Daywise Report</button>
                </div>
              </div>

              {selectedView === 'sessions' ? (
                <>
                  <div className="hidden lg:block">
                    <ChargingSessionsDesktop stationId={stationId} range={range} />
                  </div>
                  <div className="block lg:hidden">
                    <ChargingSessionsMobile stationId={stationId} range={range} />
                  </div>
                </>
              ) : selectedView === 'transactions' ? (
                <>
                  <div className="hidden lg:block">
                    <PaymentTransactionsDesktop stationId={stationId} range={range} />
                  </div>
                  <div className="block lg:hidden">
                    <PaymentTransactionsMobile stationId={stationId} range={range} />
                  </div>
                </>
              ) : (
                <>
                  <div className="hidden lg:block">
                    <DateWiseStationData stationId={stationId} range={range} />
                  </div>
                  <div className="block lg:hidden">
                    <DateWiseStationData stationId={stationId} range={range} />
                  </div>
                </>
              )}

            </div>
          </div>
    </div>
  );
};

export default StationDetails;
